import { useEffect } from "react";
import { useParams, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useHMRStableRef } from "@/hooks/useHMRStable";
import { localeNeutralPublicEventPath } from "@/routes/public";

const SUPPORTED_LOCALES = ['ro', 'en'];
const DEFAULT_LOCALE = 'ro';

export function LanguageRoute() {
  const { locale: localeParam } = useParams<{ locale?: string }>();
  const { changeLanguage, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Routes are declared with literal prefixes ("/ro/*", "/en/*"), so there is
  // no :locale param — derive the locale from the first path segment instead.
  // Without this, useParams().locale is always undefined and every /en/* URL
  // was redirected to /ro, losing the path.
  const firstSegment = location.pathname.split('/')[1] || undefined;
  const locale = localeParam ?? firstSegment;

  // JUB-81: /{locale}/@{slug} → /@{slug} (computed before the effect so the
  // effect never switches the app language for a URL we are about to leave).
  const publicEventRedirect = localeNeutralPublicEventPath(location.pathname, location.search, location.hash);

  // Track last fully validated locale to prevent redundant redirects
  // Use HMR-stable ref to persist across Vite HMR remounts
  const lastValidLocaleRef = useHMRStableRef<string | null>('language-route-last-valid-locale', null);

  useEffect(() => {
    const path = location.pathname;

    // Skip processing if path starts with @ (event slug route)
    // This should not happen if routes are ordered correctly, but add as safety check
    if (path.startsWith('/@') || publicEventRedirect) {
      // If we're here, the route was incorrectly matched - don't render anything
      // Let the /@:slug route handle it
      return;
    }

    // 1. No locale in URL → redirect to "/ro"
    if (!locale) {
      // Only redirect if we're not already on a valid path
      if (path !== `/${DEFAULT_LOCALE}` && !path.startsWith(`/${DEFAULT_LOCALE}/`)) {
        navigate(`/${DEFAULT_LOCALE}`, { replace: true });
      }
      return;
    }

    // Skip if locale is @ (should not happen, but safety check)
    if (locale === '@') {
      // Don't redirect, just return - let the /@:slug route handle it
      return;
    }

    const isValidLocale = SUPPORTED_LOCALES.includes(locale);

    // 2. Locale is valid → update language if needed, but don't redirect
    if (isValidLocale) {
      // Prevent useless redirect loops
      if (lastValidLocaleRef.current !== locale) {
        lastValidLocaleRef.current = locale;
        if (currentLanguage !== locale) {
          changeLanguage(locale);
        }
      }
      // Exit early - let nested routes render via <Outlet />
      // DO NOT navigate or redirect - just let React Router handle the nested routes
      return;
    }

    // 3. Locale is invalid → redirect to default locale, preserving the path
    const pathWithoutLocale = path.replace(/^\/[^/]+/, ""); // remove first segment

    const target = `/${DEFAULT_LOCALE}${pathWithoutLocale}`;
    // Only redirect if target is different from current path
    if (target !== path) {
      navigate(target, { replace: true });
    }
  }, [locale, location.pathname, navigate, changeLanguage, currentLanguage, publicEventRedirect]);

  // JUB-81: /en/@slug, /ro/@slug → /@slug (replace, so Back skips the hop).
  if (publicEventRedirect) {
    return <Navigate to={publicEventRedirect} replace />;
  }

  // If path starts with /@, don't render Outlet (let /@:slug route handle it)
  if (location.pathname.startsWith('/@')) {
    return null;
  }

  return <Outlet />;
}
